import { X, Star, StarHalf, ShoppingCart, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { PurchaseConfirmationModal } from "@/components/cart/PurchaseConfirmationModal";
import { useState } from "react";

interface ProductDetailsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  product: {
    id: string;
    title: string;
    description: string;
    price: string;
    rating: number;
    reviewCount: number;
    imageUrl: string;
    badge?: string;
    badgeVariant?: "default" | "secondary" | "destructive";
  };
}

export function ProductDetailsModal({
  isOpen,
  onOpenChange,
  product
}: ProductDetailsModalProps) {
  const { addItem } = useCart();
  const { toast } = useToast();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      title: product.title,
      description: product.description,
      price: product.price,
      imageUrl: product.imageUrl
    });
    
    toast({
      title: "Produto adicionado!",
      description: `${product.title} foi adicionado ao seu carrinho.`,
    });
    
    onOpenChange(false);
    setShowPurchaseModal(true);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<StarHalf key="half" className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />);
    }

    return stars;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-left">
              {product.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Imagem do Produto */}
              <div className="relative">
                <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                  <img
                    src={product.imageUrl}
                    alt={product.title}
                    className="w-full h-full object-cover hover-scale"
                  />
                  {product.badge && (
                    <Badge
                      variant={
                        product.badgeVariant === "default" 
                          ? "default" 
                          : product.badgeVariant === "secondary" 
                          ? "secondary" 
                          : "destructive"
                      }
                      className="absolute top-3 right-3"
                    >
                      {product.badge}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Detalhes do Produto */}
              <div className="space-y-6">
                {/* Avaliações */}
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {renderStars(product.rating)}
                  </div>
                <span className="text-sm text-muted-foreground">
                  {(Math.floor(product.rating * 100) / 100).toFixed(2)} ({product.reviewCount} avaliações)
                </span>
                </div>

                {/* Preço */}
                <div className="text-3xl font-bold text-primary">
                  {product.price}
                </div>

                {/* Descrição Completa */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-base">Sobre este produto</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {product.description}
                  </p>
                </div>

                {/* Características */}
                <div className="space-y-3 bg-muted/30 p-4 rounded-lg">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Características
                  </h4>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Categoria:</span>
                      <span className="font-medium">Numerologia</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Formato:</span>
                      <span className="font-medium">Digital</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Entrega:</span>
                      <span className="font-medium">Imediata</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Suporte:</span>
                      <span className="font-medium">24/7</span>
                    </div>
                  </div>
                </div>

                {/* Botão de Compra */}
                <Button 
                  onClick={handleAddToCart}
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium py-3"
                  size="lg"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Adicionar ao Carrinho
                </Button>

              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PurchaseConfirmationModal
        isOpen={showPurchaseModal}
        onOpenChange={setShowPurchaseModal}
        productTitle={product.title}
        productPrice={product.price}
      />
    </>
  );
}